import React, { useState, useEffect } from 'react';
import { HealthProfessional, Specialty } from '../../types';
import HealthProfessionalsList from './HealthProfessionalsList';
import HealthProfessionalForm from './HealthProfessionalForm';
import { service, handleApiError } from '../../services';
import { useToast } from '../common/ToastContainer';

// Mock data for specialties (these should ideally come from API too)
const mockSpecialties: Specialty[] = [
  { id: 1, name: 'PCOS', description: 'Polycystic Ovary Syndrome' },
  { id: 2, name: 'Hormonal Health', description: 'Hormone-related conditions' },
  { id: 3, name: 'Reproductive Health', description: 'Women\'s reproductive health' },
  { id: 4, name: 'Menstrual Disorders', description: 'Irregular periods and related issues' },
  { id: 5, name: 'Fertility', description: 'Fertility and conception support' },
  { id: 6, name: 'Menopause', description: 'Menopause management and support' },
  { id: 7, name: 'Contraception', description: 'Birth control and family planning' },
  { id: 8, name: 'Sexual Health', description: 'Sexual wellness and health' },
  { id: 9, name: 'Pregnancy Care', description: 'Prenatal and pregnancy support' },
  { id: 10, name: 'Mental Health', description: 'Women\'s mental health and wellness' },
  { id: 11, name: 'Endometriosis', description: 'Endometriosis diagnosis and management' },
  { id: 12, name: 'Thyroid Health', description: 'Thyroid disorders and treatment' },
  { id: 13, name: 'Breast Health', description: 'Breast health screening and care' },
  { id: 14, name: 'Pelvic Health', description: 'Pelvic floor disorders and treatment' },
  { id: 15, name: 'Adolescent Health', description: 'Teen and young adult women\'s health' }
];

const HealthProfessionalsManager: React.FC = () => {
  const toast = useToast();
  const [professionals, setProfessionals] = useState<HealthProfessional[]>([]);
  const [specialties] = useState<Specialty[]>(mockSpecialties);
  const [showForm, setShowForm] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState<HealthProfessional | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Load professionals from API on mount
  useEffect(() => {
    // Check admin authentication first
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      const user = JSON.parse(authUser);
      if (user.role !== 'admin') {
        setError('Admin access required. Please log in as an administrator.');
        return;
      }
    } else {
      setError('Authentication required. Please log in as an administrator.');
      return;
    }
    
    loadProfessionals();
  }, []);

  const testApiConnection = async () => {
    try {
      console.log('=== TESTING API CONNECTION ===');
      const authToken = localStorage.getItem('authToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      
      console.log('API URL:', apiUrl);
      console.log('Auth token present:', !!authToken);
      
      if (!authToken) {
        throw new Error('No authentication token found');
      }
      
      // Test with a simple GET request first
      const response = await fetch(`${apiUrl}/health-professionals`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ API connection successful, data:', data);
        setSuccessMessage('API connection test successful!');
      } else {
        const errorText = await response.text();
        console.error('❌ API error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (err) {
      console.error('❌ API connection test failed:', err);
      setError(`API connection test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const loadProfessionals = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await service.healthProfessionals.getAll();
      setProfessionals(data);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(`Failed to load health professionals: ${errorMessage}`);
      console.error('Failed to load professionals:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProfessional(null);
    setShowForm(true);
  };

  const handleEdit = (professional: HealthProfessional) => {
    setEditingProfessional(professional);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await service.healthProfessionals.delete(id);
      setProfessionals(prev => prev.filter(p => p.id !== id));
      toast.success('Professional deleted', 'Health professional has been removed');
    } catch (error) {
      console.error('Failed to delete professional:', error);
      toast.error('Delete failed', 'Failed to delete professional. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (professionalData: Omit<HealthProfessional, 'id' | 'createdAt' | 'updatedAt'>) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setFieldErrors({});
    
    try {
      // Check admin authentication
      const authToken = localStorage.getItem('authToken');
      const authUser = localStorage.getItem('authUser');
      
      console.log('=== ADMIN CREATE DOCTOR DEBUG ===');
      console.log('Auth token present:', !!authToken);
      console.log('Auth user present:', !!authUser);
      
      if (authUser) {
        const user = JSON.parse(authUser);
        console.log('User role:', user.role);
        console.log('User email:', user.email);
        
        if (user.role !== 'admin') {
          throw new Error('Admin privileges required to create doctors');
        }
      } else {
        throw new Error('Authentication required. Please log in as admin.');
      }
      
      if (!authToken) {
        throw new Error('Authentication token missing. Please log in again.');
      }
      
      console.log('Creating doctor with data:', professionalData);
      
      if (editingProfessional) {
        // Update existing professional
        const updated = await service.healthProfessionals.update(editingProfessional.id, professionalData);
        setProfessionals(prev => prev.map(p => 
          p.id === editingProfessional.id ? updated : p
        ));
        setSuccessMessage('Health professional updated successfully!');
      } else {
        // Create new professional
        const created = await service.healthProfessionals.create(professionalData);
        setProfessionals(prev => [...prev, created]);
        
        // Handle success message with email verification info
        if (created.emailVerificationSent) {
          setSuccessMessage(
            `Doctor profile created successfully! A password reset email has been sent to ${professionalData.email}. The doctor must use the reset link to set their password before they can login.`
          );
        } else {
          setSuccessMessage(
            `Doctor profile created successfully! Please ensure ${professionalData.email} is notified to set up their login credentials.`
          );
        }
      }
      
      setShowForm(false);
      setEditingProfessional(null);
    } catch (err: any) {
      console.error('Failed to save professional:', err);
      
      // Handle specific error types
      if (err.message && err.message.includes('email already exists')) {
        setFieldErrors({ email: 'A user with this email already exists' });
      } else if (err.message && err.message.includes('valid email')) {
        setFieldErrors({ email: 'Please provide a valid email address' });
      } else if (err.message && err.message.includes('Admin privileges required')) {
        setError('Admin privileges required. Please ensure you are logged in as an administrator.');
      } else if (err.message && err.message.includes('Authentication')) {
        setError('Authentication error. Please log in again as an administrator.');
      } else if (err.status === 403) {
        setError('Access denied. Admin authentication required to create doctors.');
      } else if (err.status === 401) {
        setError('Authentication failed. Please log in again as an administrator.');
      } else {
        setError(`Failed to save professional: ${handleApiError(err)}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProfessional(null);
    setFieldErrors({});
    setSuccessMessage(null);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                API Connection Issue
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{error}</p>
                <p className="mt-1">You can still manage professionals locally, but changes won't be saved to the database until the API connection is restored.</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={loadProfessionals}
                  className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded hover:bg-yellow-200 transition-colors mr-2"
                >
                  Retry Connection
                </button>
                <button
                  onClick={() => {
                    const authToken = localStorage.getItem('authToken');
                    const authUser = localStorage.getItem('authUser');
                    console.log('=== ADMIN DEBUG INFO ===');
                    console.log('Auth token:', authToken ? `${authToken.substring(0, 30)}...` : 'Missing');
                    console.log('Auth user:', authUser ? JSON.parse(authUser) : 'Missing');
                    console.log('API URL:', process.env.REACT_APP_API_URL);
                  }}
                  className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded hover:bg-blue-200 transition-colors mr-2"
                >
                  Debug Auth
                </button>
                <button
                  onClick={testApiConnection}
                  className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded hover:bg-green-200 transition-colors"
                >
                  Test API
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Success!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{successMessage}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded hover:bg-green-200 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <HealthProfessionalsList
        professionals={professionals}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={isLoading}
      />

      {showForm && (
        <HealthProfessionalForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          specialties={specialties}
          isLoading={isLoading}
          editingProfessional={editingProfessional}
          fieldErrors={fieldErrors}
        />
      )}
    </div>
  );
};

export default HealthProfessionalsManager;